// DraftOverview.js
// Pre-draft screen shown the first time a sport is drafted each day. Mirrors
// SportOverviewView.swift's purpose: "{SPORT} DRAFTS" header → roster positions
// → chemistry bonus info → BEGIN DRAFT. (Web uses a clean positions grid rather
// than the app's per-sport formation diagram.) Subsequent plays bypass this and
// reopen the completed view, matching the app.

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import wordmark from "../assets/Rosterable.png";
import { SPORT_CONFIG } from "../managers/draftManager";
import { useAuth } from "../managers/AuthContext";

const HIGHLIGHT = "#d4f53c"; // app theme — used for the same-team-and-year bonus

// Maximum gap between the last content card and the BEGIN button. The spacer
// below grows up to this distance, then stops — so the button never drifts to
// the bottom of a tall window. Tune this one value to move the button up/down.
const CTA_MAX_GAP = "32px";

// Sports with an explicit centered-row layout. Football isn't listed — it falls
// back to a simple wrapping grid of its SPORT_CONFIG positions (its original
// look). Slot counts match SPORT_CONFIG in draftManager.
const POSITION_LAYOUT = {
  soccer:     [["GK"], ["DEF", "DEF", "DEF", "DEF"], ["MID", "MID", "MID"], ["W", "W", "ST"]],
  baseball:   [["C"], ["1B", "2B", "3B", "SS"], ["LF", "CF", "RF", "DH"], ["SP", "SP", "RP"]],
  basketball: [["PG", "SG", "SF", "PF", "C"], ["G", "G", "F", "F", "C"]],
  hockey:     [["LW", "C", "RW"], ["LD", "RD"], ["G"], ["LW", "C", "RW"], ["LD", "RD"]],
};

// Chemistry bonuses — mirrors SportChemistryInfoView.swift.
const CHEMISTRY = [
  { count: 2, label: "Same Team", bonus: "+5%", highlight: false },
  { count: 3, label: "Same Team (3+)", bonus: "+8%", highlight: false },
  { count: 2, label: "Same Team & Year", bonus: "+15%", highlight: true },
];

export default function DraftOverview() {
  const navigate = useNavigate();
  const location = useLocation();
  const sport = location.state?.sport;

  // Signed-in users and guests start the draft immediately. A brand-new visitor
  // (no account, not yet a guest) sees a one-time nudge first. Choosing
  // "Continue as guest" flips them into the app's existing guest state, so they
  // won't be nudged again.
  const { user, isGuest, continueAsGuest } = useAuth();
  const [showNudge, setShowNudge] = useState(false);

  useEffect(() => { if (!sport) navigate("/"); }, [sport, navigate]);
  if (!sport) return null;

  function startDraft() {
    navigate("/draft", { state: { sport } });
  }

  function handleBegin() {
    if (user || isGuest) startDraft();
    else setShowNudge(true);
  }

  function proceedAsGuest() {
    continueAsGuest();
    setShowNudge(false);
    startDraft();
  }

  const layout = POSITION_LAYOUT[sport.id];                       // rows, or undefined (football)
  const flatPositions = SPORT_CONFIG[sport.id]?.positions || [];  // used when there's no row layout
  const slotCount = layout ? layout.reduce((n, row) => n + row.length, 0) : flatPositions.length;

  return (
    <div style={styles.container}>
      {/* ── Header ── */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate("/")}>‹</button>
        <button style={styles.wordmarkBtn} onClick={() => navigate("/")} aria-label="Home">
          <img src={wordmark} alt="Rosterable" style={styles.wordmark} />
        </button>
        <div style={{ width: 44 }} />
      </div>

      {/* ── Title + gradient rule ── */}
      <div style={styles.titleRow}>
        <span style={{ ...styles.title, color: sport.hex }}>
          {sport.name.toUpperCase()} DRAFTS
        </span>
        <div style={{ ...styles.titleRule, background: `linear-gradient(to right, ${sport.hex}8c, transparent)` }} />
      </div>

      {/* ── Roster positions ── */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <span style={styles.cardHeaderText}>YOUR ROSTER</span>
          <span style={{ ...styles.cardHeaderCount, color: sport.hex }}>{slotCount} PLAYERS</span>
        </div>
        {layout ? (
          <div style={styles.posLayout}>
            {layout.map((row, ri) => (
              <div key={ri} style={styles.posRow}>
                {row.map((pos, ci) => (
                  <div key={ci} style={{ ...styles.posChip, width: "56px", borderColor: `${sport.hex}59` }}>
                    <span style={styles.posChipText}>{pos}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div style={styles.posGrid}>
            {flatPositions.map((pos, i) => (
              <div key={i} style={{ ...styles.posChip, borderColor: `${sport.hex}59` }}>
                <span style={styles.posChipText}>{pos}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Chemistry bonuses ── */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <span style={styles.cardHeaderText}>CHEMISTRY BONUS</span>
        </div>
        {CHEMISTRY.map((row, i) => {
          const accent = row.highlight ? HIGHLIGHT : sport.hex;
          return (
            <div key={i} style={styles.chemRow}>
              <div style={styles.chemSwatches}>
                {Array.from({ length: row.count }).map((_, j) => (
                  <span key={j} style={{ ...styles.chemSwatch, background: `${accent}cc` }} />
                ))}
                {row.count === 3 && <span style={styles.chemPlus}>+</span>}
              </div>
              <span style={styles.chemLabel}>{row.label}</span>
              <span style={{ ...styles.chemBonus, color: accent, borderColor: `${accent}80`, background: `${accent}1f` }}>
                {row.bonus}
              </span>
            </div>
          );
        })}
      </div>

      {/* Capped spacer: grows to at most CTA_MAX_GAP below the content, so the
          button sits a fixed distance below the cards on tall windows (never
          tracking the bottom edge). It can still shrink on short windows, which
          lets the button move UP to stay on screen — but never further down. */}
      <div style={{ flex: 1, maxHeight: CTA_MAX_GAP }} />

      {/* ── Begin ── */}
      <BeginButton sport={sport} onClick={handleBegin} />

      {/* ── One-time sign-in nudge for brand-new visitors ── */}
      {showNudge && (
        <LoginNudge
          sport={sport}
          onSignIn={() => navigate("/login")}
          onGuest={proceedAsGuest}
          onClose={() => setShowNudge(false)}
        />
      )}
    </div>
  );
}

// ─── Login nudge ──────────────────────────────────────────────────────────────
// Brief, dismissible prompt shown the first time a visitor without an account
// starts a draft. Not a wall — "Continue as guest" lets them play right away.

function LoginNudge({ sport, onSignIn, onGuest, onClose }) {
  return (
    <div style={styles.nudgeOverlay} onClick={onClose}>
      <div style={styles.nudgeCard} onClick={(e) => e.stopPropagation()}>
        <span style={styles.nudgeTitle}>Save your progress</span>
        <p style={styles.nudgeBody}>
          Create a free account to save your scores, track your badges, and climb
          the leaderboards. You can keep playing as a guest if you'd rather.
        </p>

        <button
          style={{
            ...styles.nudgePrimary,
            background: `linear-gradient(to right, ${sport.hex}, ${sport.hex}cc)`,
            boxShadow: `0 6px 18px ${sport.hex}59`,
          }}
          onClick={onSignIn}
        >
          SIGN IN / SIGN UP
        </button>

        <button style={styles.nudgeSecondary} onClick={onGuest}>
          Continue as guest
        </button>
      </div>
    </div>
  );
}

function BeginButton({ sport, onClick }) {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        ...styles.beginBtn,
        background: `linear-gradient(to right, ${sport.hex}, ${sport.hex}cc)`,
        boxShadow: `0 8px 22px ${sport.hex}66`,
        transform: pressed ? "scale(0.98)" : "scale(1)",
        transition: "transform 0.1s ease",
      }}
    >
      BEGIN DRAFT
    </button>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    width: "100%",
    maxWidth: "480px",
    margin: "0 auto",
    padding: "0 16px 24px",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "52px",
    paddingBottom: "16px",
  },
  backBtn: {
    background: "none", border: "none", color: "rgba(255,255,255,0.8)",
    fontSize: "28px", fontWeight: "300", lineHeight: 1, padding: "4px 8px 4px 0", width: "44px", cursor: "pointer",
  },
  wordmark: { height: "21px", width: "auto" },
  wordmarkBtn: {
    background: "none", border: "none", padding: 0, cursor: "pointer",
    display: "flex", alignItems: "center",
  },

  titleRow: { display: "flex", alignItems: "center", gap: "10px", paddingTop: "6px", paddingBottom: "16px" },
  title: { fontSize: "19px", fontWeight: "500", letterSpacing: "2.5px", whiteSpace: "nowrap" },
  titleRule: { flex: 1, height: "1px" },

  card: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    padding: "12px 14px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginBottom: "10px",
  },
  cardHeader: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  cardHeaderText: { fontSize: "10px", fontWeight: "800", color: "rgba(255,255,255,0.5)", letterSpacing: "1.2px" },
  cardHeaderCount: { fontSize: "10px", fontWeight: "800", letterSpacing: "1px" },

  posGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(52px, 1fr))",
    gap: "8px",
  },
  posLayout: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  posRow: {
    display: "flex",
    justifyContent: "center",
    gap: "8px",
  },
  posChip: {
    height: "40px",
    borderRadius: "10px",
    border: "1px solid",
    background: "rgba(255,255,255,0.04)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  posChipText: { fontSize: "14px", fontWeight: "800", color: "rgba(255,255,255,0.88)", letterSpacing: "0.5px" },

  chemRow: { display: "flex", alignItems: "center", gap: "12px" },
  chemSwatches: { display: "flex", alignItems: "center", gap: "3px", width: "52px", flexShrink: 0 },
  chemSwatch: { width: "12px", height: "12px", borderRadius: "3px" },
  chemPlus: { fontSize: "12px", fontWeight: "800", color: "rgba(255,255,255,0.5)", marginLeft: "1px" },
  chemLabel: { fontSize: "13px", color: "rgba(255,255,255,0.8)", flex: 1 },
  chemBonus: {
    fontSize: "13px",
    fontWeight: "800",
    padding: "3px 10px",
    borderRadius: "999px",
    border: "1px solid",
    flexShrink: 0,
  },

  beginBtn: {
    width: "100%",
    border: "none",
    borderRadius: "16px",
    padding: "16px 24px",
    color: "#fff",
    fontSize: "18px",
    fontWeight: "800",
    letterSpacing: "1px",
    cursor: "pointer",
  },

  // ── Login nudge ──
  nudgeOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    backdropFilter: "blur(3px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    zIndex: 200,
  },
  nudgeCard: {
    width: "100%",
    maxWidth: "340px",
    background: "rgb(20,20,32)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "20px",
    padding: "22px 20px 16px",
    boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  nudgeTitle: {
    fontSize: "18px",
    fontWeight: "800",
    color: "#fff",
    letterSpacing: "0.3px",
  },
  nudgeBody: {
    fontSize: "13.5px",
    lineHeight: 1.5,
    color: "rgba(255,255,255,0.65)",
    margin: "10px 0 18px",
  },
  nudgePrimary: {
    width: "100%",
    border: "none",
    borderRadius: "14px",
    padding: "14px 20px",
    color: "#fff",
    fontSize: "15px",
    fontWeight: "800",
    letterSpacing: "0.8px",
    cursor: "pointer",
  },
  nudgeSecondary: {
    width: "100%",
    background: "none",
    border: "none",
    color: "rgba(255,255,255,0.55)",
    fontSize: "14px",
    fontWeight: "600",
    padding: "12px",
    marginTop: "4px",
    cursor: "pointer",
  },
};
